import { constants } from "../constantSuggestionsProvider";
import { expressionSuggestions } from "../expressionSuggestionsProvider";
import { functions } from "../functionSuggestionsProvider";
import { toSuggestion } from "../librarySuggestionsProvider/toSuggestion";
import { symbolJson } from "../operatorSymbolSuggestionsProvider";
import { serviceFunctions } from "../serviceFunctionSuggestionsProvider";
import {
  functionsVariables,
  mathVariables,
  physicsVariables,
} from "../variableSuggestionsProvider";

import { symbolVariables } from "./symbolLookup";

import type { SingleSuggestion } from "@/components/suggestions/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { MigratedSource } from "@/models/LibrariesModel";
import type { LibraryItem } from "@corca/data";

/**
 * The rows the typed providers build from each file. The operator provider's rows cover both
 * operator files, so the supplemental file adds none.
 */
const FILE_ROWS: Record<MigratedSource, SingleSuggestion[]> = {
  "expressions.json": expressionSuggestions,
  "functions.json": [...functions, ...serviceFunctions, ...functionsVariables],
  "math-variables.json": mathVariables,
  "physics-variables.json": physicsVariables,
  "constants.json": constants,
  "symbols.json": symbolVariables,
  "operator-symbols.json": symbolJson,
  "supplemental-operator-symbols.json": [],
};

const rowsByServedItems = new WeakMap<LibraryItem[], Map<string, SingleSuggestion[]>>();

/**
 * The suggestions of the given source files as the document gets them: the library's served
 * items from a file once it is served, the file's typed rows until then. The same array comes
 * back until the served items change, so readers can cache by it.
 */
export function servedSuggestions(
  doc: DocumentModel,
  sources: MigratedSource[],
): SingleSuggestion[] {
  const { servedItems } = doc.libraries;
  const bySources = rowsByServedItems.get(servedItems) ?? new Map<string, SingleSuggestion[]>();
  const key = sources.join(" ");
  let rows = bySources.get(key);

  if (!rows) {
    rows = sources.flatMap((source) =>
      doc.libraries.serves(source)
        ? servedItems.filter((item) => item.__source === source).map(toSuggestion)
        : FILE_ROWS[source],
    );
    bySources.set(key, rows);
    rowsByServedItems.set(servedItems, bySources);
  }

  return rows;
}
